<template>
  <div id="linkedin_analytics_grid">
    <InfoGrid :columns="3">
      <ContainerInfoItem
        v-for="(data, key) in analyticsData"
        :key="key"
        source="super_agi_linkedin_activity_analytics"
        :title="data.title"
        :description="range?.label || ''"
        :value="formatAnalyticsValue(data.value, data.unit)"
        :is-loading="data.isLoading"
      />
    </InfoGrid>
  </div>
</template>

<script setup>
import { computed, reactive, watchEffect } from "vue";
import {
  formatAnalyticsValue,
  toFetchLinkedinCount,
} from "@/src/utils/analyticUtils.js";
import { linkedinCountActions } from "@/src/constants/analyticConstants.js";
import store from "@/src/store/index.js";
import InfoGrid from "@/src/components/info_components/InfoGrid.vue";
import ContainerInfoItem from "@/src/components/info_components/ContainerInfoItem.vue";
import { useAnalyticsRefresh } from "@/src/composables/useAnalyticsRefresh.js";

const range = computed(() => store.getters["analytics/getRange"]);
const filters = computed(() => store.getters["analytics/getFilters"]);

const analyticsData = reactive({
  connections_sent: { title: "Connections Sent", value: 0, isLoading: true },
  in_mail_sent: { title: "InMails Sent", value: 0, isLoading: true },
  messages_sent: { title: "Messages Sent", value: 0, isLoading: true },
  in_mail_replies: { title: "InMail Replied", value: 0, isLoading: true },
  message_replies: { title: "Message Replies", value: 0, isLoading: true },
});

const getParams = () => ({
  start_time: range.value?.start,
  end_time: range.value?.end,
});

const updateAnalyticItem = (key, value) => {
  if (analyticsData[key]) {
    analyticsData[key].value = value ?? 0;
    analyticsData[key].isLoading = false;
  }
};

const fetchAnalytics = async () => {
  const params = getParams();
  Object.values(analyticsData).forEach((item) => (item.isLoading = true));

  toFetchLinkedinCount({
    ...params,
    action: linkedinCountActions.CONNECTION_SENT,
  })
    .then((result) => updateAnalyticItem("connections_sent", result))
    .catch(() => updateAnalyticItem("connections_sent", 0));

  toFetchLinkedinCount({
    ...params,
    action: linkedinCountActions.INMAIL_SENT,
  })
    .then((result) => updateAnalyticItem("in_mail_sent", result))
    .catch(() => updateAnalyticItem("in_mail_sent", 0));

  toFetchLinkedinCount({
    ...params,
    action: linkedinCountActions.MESSAGE_SENT,
  })
    .then((result) => updateAnalyticItem("messages_sent", result))
    .catch(() => updateAnalyticItem("messages_sent", 0));

  toFetchLinkedinCount({
    ...params,
    action: linkedinCountActions.INMAIL_REPLIED,
  })
    .then((result) => updateAnalyticItem("in_mail_replies", result))
    .catch(() => updateAnalyticItem("in_mail_replies", 0));

  toFetchLinkedinCount({
    ...params,
    action: linkedinCountActions.MESSAGE_REPLIED,
  })
    .then((result) => updateAnalyticItem("message_replies", result))
    .catch(() => updateAnalyticItem("message_replies", 0));
};

watchEffect(() => {
  if (range.value) fetchAnalytics();
});

useAnalyticsRefresh(fetchAnalytics, [range]);
</script>
